import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./hooks/useAuth";
import Layout from "./components/Layout";

const Home = lazy(() => import("./pages/Home"));
const Services = lazy(() => import("./pages/Services"));
const PaintProtectionFilm = lazy(() => import("./pages/services/PaintProtectionFilm"));
const CeramicCoatings = lazy(() => import("./pages/services/CeramicCoatings"));
const WindowTint = lazy(() => import("./pages/services/WindowTint"));
const VinylWraps = lazy(() => import("./pages/services/VinylWraps"));
const WindshieldProtection = lazy(() => import("./pages/services/WindshieldProtection"));
const PPFSummerlin = lazy(() => import("./pages/services/PPFSummerlin"));
const PPFQuoteLanding = lazy(() => import("./pages/PPFQuoteLanding"));
const CeramicCoatingHenderson = lazy(() => import("./pages/services/CeramicCoatingHenderson"));
const TeslaProtection = lazy(() => import("./pages/TeslaProtection"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Resources = lazy(() => import("./pages/Resources"));
const BlogPost = lazy(() => import("./pages/resources/BlogPost"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Admin = lazy(() => import("./pages/admin/Admin"));
const GalleryManagement = lazy(() => import("./pages/admin/GalleryManagement"));
const Submissions = lazy(() => import("./pages/admin/Submissions"));
const TestimonialManagement = lazy(() => import("./pages/admin/TestimonialManagement"));
const VideoManagement = lazy(() => import("./pages/admin/VideoManagement"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const LoadingFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-accent animate-pulse text-lg font-heading">Loading...</div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<Layout />}>
                <Route index element={<Home />} />
                <Route path="services" element={<Services />} />
                <Route path="services/paint-protection-film" element={<PaintProtectionFilm />} />
                <Route path="services/ceramic-coatings" element={<CeramicCoatings />} />
                <Route path="services/window-tint" element={<WindowTint />} />
                <Route path="services/vinyl-wraps" element={<VinylWraps />} />
                <Route path="services/windshield-protection" element={<WindshieldProtection />} />
                <Route path="services/window-tint-wraps" element={<Navigate to="/services/window-tint" replace />} />
                <Route path="ppf-in-summerlin" element={<PPFSummerlin />} />
                <Route path="ceramic-coating-henderson" element={<CeramicCoatingHenderson />} />
                <Route path="tesla-protection" element={<TeslaProtection />} />
                <Route path="ppf-quote" element={<PPFQuoteLanding />} />
                <Route path="gallery" element={<Gallery />} />
                <Route path="resources" element={<Resources />} />
                <Route path="resources/:slug" element={<BlogPost />} />
                <Route path="about" element={<About />} />
                <Route path="contact" element={<Contact />} />
                <Route path="thank-you" element={<ThankYou />} />
                <Route path="*" element={<NotFound />} />
              </Route>
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/gallery" element={<GalleryManagement />} />
              <Route path="/admin/submissions" element={<Submissions />} />
              <Route path="/admin/testimonials" element={<TestimonialManagement />} />
              <Route path="/admin/videos" element={<VideoManagement />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
